import { cn } from '@/lib/utils'
import Card from 'app/frontend/base/components/Card'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { getSpecializationsByClass } from 'app/frontend/base/services/characterValues'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import ItemLevelBadge from '../../TeamRoster/components/ItemLevelBadge'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import { useTeamEventParticipantsContext } from '../context/TeamEventParticipantsContext'
import EventActivitySelectedCount from './EventActivitySelectedCount'

type Props = {
  entry: EventActivityParticipantEntry
  activityId?: number
  className?: string
  contentOnly?: boolean
  showSelectedCount?: boolean
}

const EventActivityParticipantPlanningInfo = ({
  entry,
  activityId,
  className,
  contentOnly = false,
  showSelectedCount = false,
}: Props) => {
  const { characterDetailsById, activeActivityIds } =
    useTeamEventParticipantsContext()
  const character = resolveEventParticipantCharacter(entry, activityId)
  const characterDetails = characterDetailsById[character.id.toString()]
  const classSpecializations = getSpecializationsByClass(character.classId)
  const teamMembership =
    entry.participant.teamMembership ??
    entry.availableTeamMemberships.find(
      (membership) => membership.characterReference.id === character.id,
    )
  const offspecSpecs = (teamMembership?.offspecIds ?? [])
    .map((specId) => ({
      specId,
      icon: classSpecializations[specId]?.icon,
    }))
    .filter(
      (spec): spec is { specId: number; icon: string } =>
        typeof spec.icon === 'string',
    )
  const selectedCount = Object.entries(entry.selections).filter(
    ([candidateActivityId, selection]) =>
      activeActivityIds.has(Number(candidateActivityId)) && selection.selected,
  ).length

  const content = (
    <div
      className={cn(
        'grid gap-1.5 min-h-[16px]',
        showSelectedCount ? 'grid-cols-4' : 'grid-cols-3',
      )}
    >
      {showSelectedCount ? (
        <span className='flex items-center gap-1 text-left'>
          <span className='text-muted-foreground text-[10px] font-semibold'>
            in
          </span>
          <EventActivitySelectedCount
            selectedCount={selectedCount}
            className='text-xs font-mono'
          />
        </span>
      ) : null}
      <span
        className={cn(
          'flex items-center gap-1 text-left',
          offspecSpecs.length === 0 ? 'opacity-0' : '',
        )}
      >
        {offspecSpecs.map((spec) => (
          <Tooltip key={spec.specId}>
            <TooltipTrigger>
              <BlizzardIcon iconName={spec.icon} size='small' />
            </TooltipTrigger>
            <TooltipContent className='flex flex-col items-center'>
              <span className='text-muted-foreground text-xs'>Off spec</span>
              <span>{classSpecializations[spec.specId]?.name}</span>
            </TooltipContent>
          </Tooltip>
        ))}
      </span>

      <span
        className={cn(
          'flex items-center gap-1 justify-center',
          entry.availableTeamMemberships.length <= 1 ? 'opacity-0' : '',
        )}
      >
        <span className='text-muted-foreground text-[10px] font-semibold'>
          alts
        </span>
        <span>{entry.availableTeamMemberships.length - 1}</span>
      </span>

      <ItemLevelBadge
        details={characterDetails}
        className='bg-transparent border-none hover:bg-transparent px-0 justify-end'
      />
    </div>
  )

  if (contentOnly) return content

  return (
    <Card className={cn('px-1.5 pt-[2px] pb-px text-xs', className)}>
      {content}
    </Card>
  )
}

export default EventActivityParticipantPlanningInfo
